import React, { useEffect, useState } from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Badge, Button, AsyncSection } from "../ds";
import tokens from "../designTokens";
import { MONO } from "../tableStyles";
import { qualityEpisodes, qualityEpisode } from "./api";
import { GOAL_TONE, CLAIM_TONE, fmtTime } from "./shared";

const useStyles = makeStyles({
  item: { border: `1px solid ${tokens.border}`, borderRadius: 6, padding: "10px 12px", marginBottom: 8 },
  meta: { fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint },
  event: { fontFamily: tokens.fontBody, fontSize: 12.5, lineHeight: 1.5, color: tokens.textPrimary, margin: "6px 0" },
  kind: { color: tokens.textHint, fontWeight: 600, marginRight: 6 },
  mono: { fontFamily: MONO, fontSize: 11, color: tokens.textSecondary, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 120, overflow: "auto" },
});

const clip = (v) => {
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s && s.length > 600 ? `${s.slice(0, 600)}…` : s;
};

export function EpisodeTrace({ episodeId }) {
  const classes = useStyles();
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });

  useEffect(() => {
    let alive = true;
    qualityEpisode(episodeId).then((r) => alive && setState({ ...r, loading: false }));
    return () => {
      alive = false;
    };
  }, [episodeId]);

  const events = state.data?.events || [];
  const claims = state.data?.verdict?.critical_claims || [];
  const claimsBySeq = {};
  claims.forEach((c) => {
    const seq = Number(String(c.claim_ref).split(":")[1]?.split("#")[0]);
    (claimsBySeq[seq] = claimsBySeq[seq] || []).push(c);
  });

  return (
    <AsyncSection
      loading={state.loading}
      notFound={state.notFound}
      error={state.error}
      empty={!events.length}
      emptyTitle={state.data?.events_unavailable ? "Transcript not available for this episode" : "No transcript events"}
    >
      <div>
        {state.data?.verdict?.knowledge_missing && (
          <Badge tone="warning" subtle style={{ marginBottom: 6 }}>knowledge missing</Badge>
        )}
        {events.map((e) => (
          <div key={`${e.seq}-${e.kind}-${e.call_id || ""}`} className={classes.event}>
            <span className={classes.kind}>#{e.seq} {e.kind}{e.tool ? ` · ${e.tool}` : ""}</span>
            {e.text}
            {e.args && <div className={classes.mono}>{clip(e.args)}</div>}
            {e.result && <div className={classes.mono}>{clip(e.result)}</div>}
            {(claimsBySeq[e.seq] || []).map((c) => (
              <Badge key={c.claim_ref} tone={CLAIM_TONE[c.verdict] || "neutral"} subtle style={{ marginRight: 6, marginTop: 4 }} title={c.claim_ref}>
                {c.claim_ref} · {c.verdict}
              </Badge>
            ))}
          </div>
        ))}
      </div>
    </AsyncSection>
  );
}

const PAGE = 20;

export default function EpisodesPanel({ projectId, range }) {
  const classes = useStyles();
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [more, setMore] = useState({ loading: false, error: null });
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    setState({ loading: true, notFound: false, error: null, data: null });
    setItems([]);
    setNext(null);
    qualityEpisodes(projectId, { from: range.from, to: range.to, limit: PAGE }).then((r) => {
      if (!alive) return;
      setItems(r.data?.episodes || []);
      setNext(r.data?.next_before || null);
      setState({ ...r, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [projectId, range.from, range.to]);

  const loadMore = async () => {
    setMore({ loading: true, error: null });
    const r = await qualityEpisodes(projectId, { from: range.from, to: range.to, limit: PAGE, before: next });
    if (r.error || r.notFound) {
      setMore({ loading: false, error: r.error || "Not available" });
      return;
    }
    setItems((cur) => [...cur, ...(r.data?.episodes || [])]);
    setNext(r.data?.next_before || null);
    setMore({ loading: false, error: null });
  };

  return (
    <AsyncSection loading={state.loading} notFound={state.notFound} error={state.error} empty={!items.length} emptyTitle="No judged episodes in range">
      <div>
        {items.map((ep) => (
          <div key={ep._id} className={classes.item}>
            <Box display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
              <Typography component="span" className={classes.meta}>{fmtTime(ep.judged_at)} · {ep.turns ?? "—"} turns</Typography>
              {ep.status === "judge_failed" ? (
                <Badge tone="error" subtle>
                  judge failed{ep.error ? `: ${ep.error}` : ""}{ep.fail_count > 1 ? ` (x${ep.fail_count})` : ""}
                </Badge>
              ) : (
                <>
                  <Badge tone={GOAL_TONE[ep.goal] || "neutral"} subtle>goal {ep.goal}</Badge>
                  <Badge tone={ep.handoff === "missed" ? "error" : "neutral"} subtle>handoff {ep.handoff}</Badge>
                  <Badge tone={ep.language_ok === false ? "warning" : "neutral"} subtle>
                    language {ep.language_ok == null ? "—" : ep.language_ok ? "ok" : "mismatch"}
                  </Badge>
                  {ep.knowledge_missing && <Badge tone="warning" subtle>knowledge missing</Badge>}
                </>
              )}
              {ep.motion && <Badge tone="neutral" subtle>{ep.motion}</Badge>}
              {(ep.issues || []).map((i) => <Badge key={i} tone="warning" subtle>{i}</Badge>)}
              <span style={{ marginLeft: "auto" }}>
                <Button size="sm" variant="text" onClick={() => setOpen(open === ep._id ? null : ep._id)}>
                  {open === ep._id ? "Hide" : "Open"}
                </Button>
              </span>
            </Box>
            {open === ep._id && <EpisodeTrace episodeId={ep._id} />}
          </div>
        ))}
        {more.error && <Typography className={classes.meta} style={{ color: "#F44336" }}>{more.error}</Typography>}
        {next && (
          <Box mt={1}>
            <Button size="sm" variant="outlined" disabled={more.loading} onClick={loadMore}>
              {more.loading ? "Loading…" : "Load more"}
            </Button>
          </Box>
        )}
      </div>
    </AsyncSection>
  );
}
